import { formatQuantityDisplay } from '@/composables/useQuantity'

function defaultFormatMods (mods) {
  const parts = (Array.isArray(mods) ? mods : []).map((m) => {
    const label = m.name_snapshot || m.name || ''
    const q = Number(m.quantity)
    if (label && Number.isFinite(q) && q > 1) return `${q}× ${label}`
    return label
  }).filter(Boolean)
  return parts.join(', ')
}

function buildTicketHtml (sale, opts) {
  const { t, fulfillmentStatusLabel, formatDate, formatMods = defaultFormatMods } = opts
  const lines = (sale.items || []).map((li) => {
    const qty = formatQuantityDisplay(li.quantity, { allowFractional: li.product?.allow_fractional })
    const mods = formatMods(li.modifiers)
    const extra = mods ? ` (${mods})` : ''
    return `<div>${qty}× ${li.product?.name || t('kds.itemFallback')}${extra}</div>`
  }).join('')
  const docTitle = t('kds.printDocTitle', { id: sale.id })
  const heading = t('kds.printHeading', { id: sale.id })
  const statusLine = fulfillmentStatusLabel(sale.fulfillment_status)
  const nameLine = sale.customer
    ? (sale.customer.display_name || sale.customer.full_name || [sale.customer.first_name, sale.customer.last_name].filter(Boolean).join(' '))
    : ''
  const phoneLine = (sale.customer?.phones || []).find((p) => p.is_primary)?.phone
    || (sale.customer?.phones && sale.customer.phones[0]?.phone)
    || ''
  const customerBlock = nameLine || phoneLine
    ? `<div class="muted">${[nameLine, phoneLine].filter(Boolean).join(' · ')}</div>`
    : ''
  const html = `
    <!DOCTYPE html><html><head><title>${docTitle}</title>
    <style>
      body { font-family: sans-serif; width: 72mm; margin: 0; padding: 8px; }
      h1 { font-size: 14px; margin: 0 0 8px; }
      .muted { color: #666; font-size: 11px; }
    </style></head><body>
    <h1>${heading}</h1>
    ${customerBlock}
    <div class="muted">${formatDate(sale.created_at)}</div>
    <div class="muted">${statusLine}</div>
    <hr/>
    ${lines}
    </body></html>`
  return { html, docTitle }
}

/**
 * Print via a hidden iframe so the system print dialog is not tied to a tab
 * that we close immediately (window.print() is non-blocking; closing the
 * window right away cancels the dialog). Same approach works from drawers/dialogs
 * without popup blockers.
 *
 * @param {object} sale
 * @param {object} opts
 * @param {Function} opts.t - i18n t
 * @param {Function} opts.fulfillmentStatusLabel
 * @param {Function} opts.formatDate
 * @param {Function} [opts.formatMods]
 */
export function printKdsTicket (sale, opts) {
  const { html, docTitle } = buildTicketHtml(sale, opts)

  const iframe = document.createElement('iframe')
  iframe.setAttribute('title', docTitle)
  iframe.setAttribute('aria-hidden', 'true')
  iframe.style.cssText = 'position:fixed;width:0;height:0;border:0;left:0;top:0;opacity:0;pointer-events:none'
  document.body.appendChild(iframe)

  const win = iframe.contentWindow
  if (!win) {
    iframe.remove()
    return
  }

  const doc = win.document
  doc.open()
  doc.write(html)
  doc.close()

  let cleaned = false
  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    try {
      win.removeEventListener('afterprint', onAfterPrint)
    } catch {
      /* ignore */
    }
    iframe.remove()
  }
  function onAfterPrint () {
    cleanup()
  }
  win.addEventListener('afterprint', onAfterPrint)

  const invokePrint = () => {
    try {
      win.focus()
      win.print()
    } catch {
      cleanup()
    }
  }

  requestAnimationFrame(invokePrint)

  window.setTimeout(() => {
    if (!cleaned) cleanup()
  }, 120000)
}
